import { useCallback, useEffect, useMemo, useState, type Ref } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Chart as ChartJS,
  Legend,
  Tooltip,
  registerables,
  type ChartData,
  type ChartOptions,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { formatInTimeZone } from 'date-fns-tz';
import { useNavigate } from '@tanstack/react-router';
import { PiDownloadSimple } from 'react-icons/pi';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import InfoError from 'src/components/atoms/info-error/info-error';
import {
  AdvancedReportsAgentProfileMode,
  AdvancedReportsCallDirection,
  AdvancedReportsTeamFilterMode,
  AdvancedReportsTimeGroupBy,
  ReportDateRangePreset,
} from 'src/api/generated';
import type {
  AdvancedReportsAgentCallsResponse,
  AdvancedReportsAgentPerformanceFilters,
  AdvancedReportsConfigResponse,
  AdvancedReportsConnectedMinutesFilters,
  AdvancedReportsExtractionsFilters,
  AdvancedReportsHeatmapRow,
  AdvancedReportsTeamUserFilters,
  AdvancedReportsTimeSeriesFilters,
  ReportDateRangeFilter,
} from 'src/api/generated';
import { useTeams } from 'hooks/api/teams';
import { useReportWidgetUsers } from 'src/modules/reports/hooks/reports-dashboard';
import { ReportsService } from 'src/api/generated/services/ReportsService';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import MultiSelect from 'src/modules/shared/components/atoms/inputs/multi-select';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import { Tab } from 'src/modules/shared/components/atoms/tabs/tab';
import { Tabs } from 'src/modules/shared/components/atoms/tabs/tabs';
import { Text } from 'src/modules/shared/components/atoms/text';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import Pagination from 'src/modules/shared/components/molecules/paginations/pagination';
import {
  Table,
  type TableColumnProps,
} from 'src/modules/shared/components/molecules/table/table';
import {
  TableSortableHeader,
  type TableSortDirection,
} from 'src/modules/shared/components/molecules/table/table-sortable-header';
import 'components/molecules/charts/chartjs-registry';
import { AdvancedReportsSkeleton } from './reports-skeletons';
import { useAgentPerformanceCsvExport } from 'src/modules/reports/hooks/use-agent-performance-csv-export';
import { isAdvancedReportQueryEnabled } from 'src/modules/reports/utils/advanced-report-query';
import { ReportDateRangePicker } from 'src/modules/reports/components/report-date-range-picker';

ChartJS.register(...registerables, Legend, Tooltip);

const SERIES_COLORS = [
  '#1D4ED8',
  '#F97316',
  '#059669',
  '#DC2626',
  '#7C3AED',
  '#0F766E',
  '#D97706',
  '#475569',
];

const REPORT_FILTER_ROW_CLASS_NAME = 'flex flex-wrap items-center gap-3';
const REPORT_SELECT_MEDIUM_WIDTH_CLASS_NAME = 'w-[220px] max-w-full flex-none';
const REPORT_SELECT_NARROW_WIDTH_CLASS_NAME = 'w-[180px] max-w-full flex-none';
const REPORT_SELECT_WIDE_WIDTH_CLASS_NAME = 'w-[280px] max-w-full flex-none';
const AGENT_PERFORMANCE_SUMMARY_GRID_CLASS_NAME =
  'grid gap-3 border-b border-neutral-100 px-4 py-4 sm:grid-cols-2 xl:grid-cols-5';

type AgentPerformanceSortDirection = TableSortDirection;

type AgentPerformanceSortField =
  | 'agentName'
  | 'teamName'
  | 'connectedCalls'
  | 'averageDurationSeconds'
  | 'minutesTalked'
  | 'totalCalls'
  | 'leadCount'
  | 'contactabilityPercent'
  | 'callContactabilityPercent'
  | 'callsPerLead'
  | 'performance';

/** Current sort field and direction for the agent performance table. */
interface AgentPerformanceSortState {
  /** Field currently controlling table order. */
  field: AgentPerformanceSortField;
  /** Direction currently controlling table order. */
  direction: AgentPerformanceSortDirection;
}

interface AgentPerformanceSummaryItem {
  label: string;
  value: string;
}

type OutgoingHeatmapMetricMode = 'volume' | 'contactability';

type WithoutDateRange<T extends { dateRange: ReportDateRangeFilter }> = Omit<
  T,
  'dateRange'
>;

const SIMPLE_LINE_OPTIONS: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
    },
  },
};

const STACKED_BAR_OPTIONS: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
    },
  },
  scales: {
    x: {
      stacked: true,
    },
    y: {
      stacked: true,
      beginAtZero: true,
    },
  },
};

const BAR_OPTIONS: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
    },
  },
};

const METRIC_HEADER_LABEL_CLASS_NAME = 'font-bold';
const METRIC_CELL_VALUE_CLASS_NAME = 'font-bold';

const buildMultiSeriesLineData = (
  series: AdvancedReportsAgentCallsResponse['series']
): ChartData<'line'> => {
  const labels = series[0]?.points.map((point) => point.label) ?? [];

  return {
    labels,
    datasets: series.map((item, index) => ({
      label: item.label,
      data: item.points.map((point) => point.value),
      borderColor: SERIES_COLORS[index % SERIES_COLORS.length],
      backgroundColor: SERIES_COLORS[index % SERIES_COLORS.length],
      tension: 0.25,
    })),
  };
};

const buildStackedBarData = (
  series: AdvancedReportsAgentCallsResponse['series']
): ChartData<'bar'> => {
  const labels = series[0]?.points.map((point) => point.label) ?? [];

  return {
    labels,
    datasets: series.map((item, index) => ({
      label: item.label,
      data: item.points.map((point) => point.value),
      backgroundColor: SERIES_COLORS[index % SERIES_COLORS.length],
    })),
  };
};

const buildSingleMetricLineData = (
  labels: string[],
  values: number[],
  label: string,
  color = SERIES_COLORS[0]
): ChartData<'line'> => ({
  labels,
  datasets: [
    {
      label,
      data: values,
      borderColor: color,
      backgroundColor: color,
      tension: 0.25,
    },
  ],
});

const buildSingleMetricBarData = (
  labels: string[],
  values: number[],
  label: string,
  color = SERIES_COLORS[1]
): ChartData<'bar'> => ({
  labels,
  datasets: [
    {
      label,
      data: values,
      backgroundColor: color,
    },
  ],
});

const percentage = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `${(value * 100).toFixed(1)}%`;

const decimal = (value: number | null | undefined, digits = 2) =>
  value === null || value === undefined ? '—' : value.toFixed(digits);

const integer = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : value.toLocaleString();

const formatReportTimestamp = (
  value: string | null | undefined,
  timezone: string
) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return formatInTimeZone(parsed, timezone, 'dd-MM-yyyy HH:mm');
};

const compareNullableNumbers = (
  left: number | null | undefined,
  right: number | null | undefined,
  direction: AgentPerformanceSortDirection
) => {
  if (left === null || left === undefined) {
    return right === null || right === undefined ? 0 : 1;
  }

  if (right === null || right === undefined) {
    return -1;
  }

  return direction === 'asc' ? left - right : right - left;
};

const compareNullableStrings = (
  left: string | null | undefined,
  right: string | null | undefined,
  direction: AgentPerformanceSortDirection,
  locale: string
) => {
  const normalizedLeft = left?.trim() ?? '';
  const normalizedRight = right?.trim() ?? '';

  if (!normalizedLeft) {
    return normalizedRight ? 1 : 0;
  }

  if (!normalizedRight) {
    return -1;
  }

  const result = normalizedLeft.localeCompare(normalizedRight, locale);
  return direction === 'asc' ? result : -result;
};

const resolveQueryError = (error: unknown) =>
  error instanceof Error ? error : null;

const withDateRange = <T extends object>(
  filters: T | null,
  dateRange: ReportDateRangeFilter
): (T & { dateRange: ReportDateRangeFilter }) | null =>
  filters ? { ...filters, dateRange } : null;

const useDeferredSectionVisibility = (
  initialVisible = false
): {
  ref: (node: HTMLElement | null) => void;
  visible: boolean;
} => {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [visible, setVisible] = useState(
    initialVisible || typeof IntersectionObserver === 'undefined'
  );

  useEffect(() => {
    if (visible || typeof IntersectionObserver === 'undefined') {
      return;
    }

    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '320px 0px',
        threshold: 0.01,
      }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [node, visible]);

  return {
    ref: setNode,
    visible,
  };
};

const SectionCard: React.FC<{
  title: string;
  description?: string;
  children: React.ReactNode;
  sectionRef?: Ref<HTMLElement>;
  actions?: React.ReactNode;
}> = ({ title, description, children, sectionRef, actions }) => {
  return (
    <section
      ref={sectionRef}
      className="min-w-0 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm"
    >
      <div className="border-b border-neutral-100 px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <Text
              as="h3"
              size="h6"
              weight="semibold"
              className="text-neutral-900"
            >
              {title}
            </Text>
            {description ? (
              <Text size="small" className="mt-1 text-neutral-500">
                {description}
              </Text>
            ) : null}
          </div>
          {actions ? (
            <div className="flex flex-none items-center">{actions}</div>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
};

const SectionContent: React.FC<{
  loading: boolean;
  error: Error | null;
  hasData?: boolean;
  skeletonHeightClassName?: string;
  children: React.ReactNode;
}> = ({
  loading,
  error,
  hasData = false,
  skeletonHeightClassName = 'h-[260px]',
  children,
}) => {
  if (loading) {
    return (
      <div className="space-y-3 px-4 py-6">
        <TextSkeleton className="h-4 w-40" />
        <div
          className={`w-full animate-pulse rounded-xl bg-neutral-100 ${skeletonHeightClassName}`}
        />
      </div>
    );
  }

  if (error && !hasData) {
    return (
      <div className="px-4 py-6">
        <InfoError error={error} />
      </div>
    );
  }

  return <>{children}</>;
};

const HeatmapTable: React.FC<{
  rows: AdvancedReportsHeatmapRow[];
  totalHeader: string;
  percentMode?: boolean;
  tableClassName?: string;
}> = ({ rows, totalHeader, percentMode = false }) => {
  const { t } = useI18n();
  const columns = [
    {
      header: t('reports.advanced.table.headers.timeRange'),
      accessor: 'intervalLabel',
    },
    ...[
      t('reports.advanced.weekdays.monday'),
      t('reports.advanced.weekdays.tuesday'),
      t('reports.advanced.weekdays.wednesday'),
      t('reports.advanced.weekdays.thursday'),
      t('reports.advanced.weekdays.friday'),
      t('reports.advanced.weekdays.saturday'),
      t('reports.advanced.weekdays.sunday'),
    ].map((dayLabel, index) => ({
      header: dayLabel,
      accessor: `day-${index}`,
      render: (_value: unknown, row: AdvancedReportsHeatmapRow) =>
        percentMode
          ? percentage(row.dayValues[index] ?? null)
          : integer(row.dayValues[index] ?? 0),
    })),
    {
      header: totalHeader,
      accessor: 'total',
      render: (value: unknown) =>
        percentMode
          ? percentage((value as number | null) ?? null)
          : integer((value as number | null) ?? 0),
    },
  ];

  return (
    <TableSection>
      <Table
        columns={columns}
        data={rows}
        keyExtractor={(row) => row.intervalKey}
        variant="secondary"
        size="xs"
        tableClassName="w-full min-w-[1080px]"
        emptyStateMessage={t('reports.advanced.table.empty')}
      />
    </TableSection>
  );
};

const TableSection: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="w-full max-w-full overflow-x-auto px-4 py-4">{children}</div>
);

const PaginatedTable = <TRow extends object>({
  columns,
  data,
  keyExtractor,
  pageSizeOptions,
  initialPageSize = 10,
  fixedFirstColumn = false,
  tableContainerClassName = '',
  tableClassName = 'w-full min-w-[960px]',
}: {
  columns: TableColumnProps<TRow>[];
  data: TRow[];
  keyExtractor: (row: TRow) => string;
  pageSizeOptions?: { content: string; value: string }[];
  initialPageSize?: number;
  fixedFirstColumn?: boolean;
  tableContainerClassName?: string;
  tableClassName?: string;
}) => {
  const { t } = useI18n();
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [data, pageSize]);

  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
  const currentPageRows = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return data.slice(startIndex, startIndex + pageSize);
  }, [currentPage, data, pageSize]);
  const resolvedPageSizeOptions =
    pageSizeOptions ??
    [10, 25, 50].map((size) => ({
      content: t('reports.advanced.pagination.rowsPerPage', { count: size }),
      value: String(size),
    }));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <>
      <TableSection>
        <Table
          columns={columns}
          data={currentPageRows}
          keyExtractor={keyExtractor}
          className={tableContainerClassName}
          fixedFirstColumn={fixedFirstColumn}
          size="xs"
          variant="secondary"
          tableClassName={tableClassName}
          emptyStateMessage={t('reports.advanced.table.empty')}
        />
      </TableSection>
      {data.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-100 px-4 py-3">
          <Text size="small" className="text-neutral-500">
            {t('reports.advanced.pagination.summary', {
              visible: currentPageRows.length,
              total: data.length,
            })}
          </Text>
          <div className="flex flex-wrap items-center gap-3">
            <Select
              size="sm"
              className="min-w-[132px]"
              containerClassName="w-auto flex-none"
              value={String(pageSize)}
              onChange={(event) => setPageSize(Number(event.target.value))}
              options={resolvedPageSizeOptions}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              size="sm"
              variant="secondary"
            />
          </div>
        </div>
      ) : null}
    </>
  );
};

const ServerPaginatedTable = <TRow extends object>({
  columns,
  data,
  keyExtractor,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  tableClassName = 'w-full min-w-[960px]',
}: {
  columns: TableColumnProps<TRow>[];
  data: TRow[];
  keyExtractor: (row: TRow) => string;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: { content: string; value: string }[];
  tableClassName?: string;
}) => {
  const { t } = useI18n();
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const resolvedPageSizeOptions =
    pageSizeOptions ??
    [10, 25, 50].map((size) => ({
      content: t('reports.advanced.pagination.rowsPerPage', { count: size }),
      value: String(size),
    }));

  return (
    <>
      <TableSection>
        <Table
          columns={columns}
          data={data}
          keyExtractor={keyExtractor}
          size="xs"
          variant="secondary"
          tableClassName={tableClassName}
          emptyStateMessage={t('reports.advanced.table.empty')}
        />
      </TableSection>
      {totalCount > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-100 px-4 py-3">
          <Text size="small" className="text-neutral-500">
            {t('reports.advanced.pagination.summary', {
              visible: data.length,
              total: totalCount,
            })}
          </Text>
          <div className="flex flex-wrap items-center gap-3">
            <Select
              size="sm"
              className="min-w-[132px]"
              containerClassName="w-auto flex-none"
              value={String(pageSize)}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
              options={resolvedPageSizeOptions}
            />
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={onPageChange}
              size="sm"
              variant="secondary"
            />
          </div>
        </div>
      ) : null}
    </>
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const AdvancedReports: React.FC = () => {
  const { language, t } = useI18n();
  const navigate = useNavigate();
  const groupByOptions = useMemo(
    () => [
      {
        label: t('reports.advanced.options.groupBy.day'),
        value: AdvancedReportsTimeGroupBy.DAY,
      },
      {
        label: t('reports.advanced.options.groupBy.week'),
        value: AdvancedReportsTimeGroupBy.WEEK,
      },
      {
        label: t('reports.advanced.options.groupBy.month'),
        value: AdvancedReportsTimeGroupBy.MONTH,
      },
    ],
    [t]
  );
  const directionOptions = useMemo(
    () => [
      {
        label: t('reports.advanced.options.direction.all'),
        value: AdvancedReportsCallDirection.BOTH,
      },
      {
        label: t('reports.advanced.options.direction.incoming'),
        value: AdvancedReportsCallDirection.INCOMING,
      },
      {
        label: t('reports.advanced.options.direction.outgoing'),
        value: AdvancedReportsCallDirection.OUTGOING,
      },
    ],
    [t]
  );
  const profileOptions = useMemo(
    () => [
      {
        label: t('reports.advanced.options.profile.agentsOnly'),
        value: AdvancedReportsAgentProfileMode.AGENTS_ONLY,
      },
      {
        label: t('reports.advanced.options.profile.activeUsersOnly'),
        value: AdvancedReportsAgentProfileMode.ACTIVE_USERS_ONLY,
      },
      {
        label: t('reports.advanced.options.profile.allUsers'),
        value: AdvancedReportsAgentProfileMode.ALL_USERS,
      },
    ],
    [t]
  );
  const sortableHeaderLabels = useMemo(
    () => ({
      sortBy: t('reports.advanced.table.sortBy'),
      ascending: t('reports.advanced.table.ascending'),
      descending: t('reports.advanced.table.descending'),
    }),
    [t]
  );
  const configQuery = useQuery<AdvancedReportsConfigResponse>({
    queryKey: ['reports', 'advanced', 'config'],
    queryFn: async () => ReportsService.getAdvancedReportsConfig(),
  });
  const { teams, loading: teamsLoading, error: teamsError } = useTeams();
  const {
    users: reportUsers,
    loading: reportUsersLoading,
    error: reportUsersError,
  } = useReportWidgetUsers(true);
  const [agentPerformanceSort, setAgentPerformanceSort] =
    useState<AgentPerformanceSortState>({
      field: 'totalCalls',
      direction: 'desc',
    });
  const agentPerformanceSortField = agentPerformanceSort.field;
  const agentPerformanceSortDirection = agentPerformanceSort.direction;

  const [filtersReady, setFiltersReady] = useState(false);
  const [reportDateRange, setReportDateRange] = useState<ReportDateRangeFilter>(
    {
      preset: ReportDateRangePreset.LAST_7_DAYS,
    }
  );
  const [agentPerformanceFilters, setAgentPerformanceFilters] =
    useState<WithoutDateRange<AdvancedReportsAgentPerformanceFilters> | null>(
      null
    );
  const [agentCallsFilters, setAgentCallsFilters] =
    useState<WithoutDateRange<AdvancedReportsTimeSeriesFilters> | null>(null);
  const [featuredCallsFilters, setFeaturedCallsFilters] =
    useState<WithoutDateRange<AdvancedReportsTimeSeriesFilters> | null>(null);
  const [outgoingHeatmapFilters, setOutgoingHeatmapFilters] =
    useState<WithoutDateRange<AdvancedReportsTeamUserFilters> | null>(null);
  const [outgoingHeatmapMetric, setOutgoingHeatmapMetric] =
    useState<OutgoingHeatmapMetricMode>('volume');
  const [objectionsFilters, setObjectionsFilters] = useState<{
    page: number;
    pageSize: number;
  } | null>(null);
  const [leadQualificationFilters, setLeadQualificationFilters] =
    useState<WithoutDateRange<AdvancedReportsTimeSeriesFilters> | null>(null);
  const [connectedMinutesFilters, setConnectedMinutesFilters] =
    useState<WithoutDateRange<AdvancedReportsConnectedMinutesFilters> | null>(
      null
    );
  const [extractionsFilters, setExtractionsFilters] =
    useState<WithoutDateRange<AdvancedReportsExtractionsFilters> | null>(null);

  useEffect(() => {
    if (!configQuery.data || filtersReady) {
      return;
    }

    setAgentPerformanceFilters({
      callDirection: AdvancedReportsCallDirection.BOTH,
      profileMode: AdvancedReportsAgentProfileMode.AGENTS_ONLY,
      teamIds: [],
    });
    setAgentCallsFilters({
      groupBy: AdvancedReportsTimeGroupBy.DAY,
    });
    setFeaturedCallsFilters({
      groupBy: AdvancedReportsTimeGroupBy.DAY,
    });
    setOutgoingHeatmapFilters({
      userIds: [],
      teamIds: [],
    });
    setObjectionsFilters({
      page: 1,
      pageSize: 10,
    });
    setLeadQualificationFilters({
      groupBy: AdvancedReportsTimeGroupBy.DAY,
    });
    setConnectedMinutesFilters({
      groupBy: AdvancedReportsTimeGroupBy.DAY,
      teamIds: [],
    });
    setExtractionsFilters({
      teamFilterMode: AdvancedReportsTeamFilterMode.ALL,
      teamId: undefined,
    });
    setFiltersReady(true);
  }, [configQuery.data, filtersReady]);

  const loading = configQuery.isLoading || !filtersReady;
  const error = resolveQueryError(configQuery.error);
  const teamsUnavailable = teamsLoading || Boolean(teamsError);
  const usersUnavailable = reportUsersLoading || Boolean(reportUsersError);

  const teamOptions = useMemo(
    () =>
      teams.map((team) => ({
        value: String(team.id),
        content:
          team.name ??
          t('reports.advanced.filters.teamFallback', { id: team.id }),
      })),
    [t, teams]
  );

  const userOptions = useMemo(
    () =>
      reportUsers.map((user) => ({
        value: String(user.id),
        content: user.label,
      })),
    [reportUsers]
  );

  const { ref: agentPerformanceRef, visible: agentPerformanceVisible } =
    useDeferredSectionVisibility(true);
  const { ref: agentCallsRef, visible: agentCallsVisible } =
    useDeferredSectionVisibility(true);
  const { ref: featuredCallsRef, visible: featuredCallsVisible } =
    useDeferredSectionVisibility(true);
  const { ref: agentHourlyRef, visible: agentHourlyVisible } =
    useDeferredSectionVisibility(false);
  const { ref: outgoingHeatmapRef, visible: outgoingHeatmapVisible } =
    useDeferredSectionVisibility(false);
  const { ref: objectionsRef, visible: objectionsVisible } =
    useDeferredSectionVisibility(false);
  const { ref: leadQualificationRef, visible: leadQualificationVisible } =
    useDeferredSectionVisibility(false);
  const { ref: connectedMinutesRef, visible: connectedMinutesVisible } =
    useDeferredSectionVisibility(false);
  const { ref: extractionsRef, visible: extractionsVisible } =
    useDeferredSectionVisibility(false);
  const {
    ref: contactabilityByPhoneRef,
    visible: contactabilityByPhoneVisible,
  } = useDeferredSectionVisibility(false);

  const agentPerformanceRequestFilters = withDateRange(
    agentPerformanceFilters,
    reportDateRange
  );
  const agentCallsRequestFilters = withDateRange(
    agentCallsFilters,
    reportDateRange
  );
  const featuredCallsRequestFilters = withDateRange(
    featuredCallsFilters,
    reportDateRange
  );
  const agentHourlyRequestFilters = filtersReady
    ? { dateRange: reportDateRange }
    : null;
  const outgoingHeatmapRequestFilters = withDateRange(
    outgoingHeatmapFilters,
    reportDateRange
  );
  const objectionsRequestFilters = withDateRange(
    objectionsFilters,
    reportDateRange
  );
  const leadQualificationRequestFilters = withDateRange(
    leadQualificationFilters,
    reportDateRange
  );
  const connectedMinutesRequestFilters = withDateRange(
    connectedMinutesFilters,
    reportDateRange
  );
  const extractionsRequestFilters = withDateRange(
    extractionsFilters,
    reportDateRange
  );
  const contactabilityByPhoneRequestFilters = filtersReady
    ? { dateRange: reportDateRange }
    : null;

  const agentPerformanceQuery = useQuery({
    queryKey: [
      'reports',
      'advanced',
      'agent-performance',
      agentPerformanceRequestFilters,
    ],
    queryFn: async () =>
      ReportsService.queryAgentPerformance(agentPerformanceRequestFilters!),
    enabled: isAdvancedReportQueryEnabled(
      agentPerformanceRequestFilters,
      agentPerformanceVisible,
      t
    ),
  });
  const agentCallsQuery = useQuery({
    queryKey: ['reports', 'advanced', 'agent-calls', agentCallsRequestFilters],
    queryFn: async () =>
      ReportsService.queryAgentCalls(agentCallsRequestFilters!),
    enabled: isAdvancedReportQueryEnabled(
      agentCallsRequestFilters,
      agentCallsVisible,
      t
    ),
  });
  const featuredCallsQuery = useQuery({
    queryKey: [
      'reports',
      'advanced',
      'featured-calls',
      featuredCallsRequestFilters,
    ],
    queryFn: async () =>
      ReportsService.queryFeaturedCalls(featuredCallsRequestFilters!),
    enabled: isAdvancedReportQueryEnabled(
      featuredCallsRequestFilters,
      featuredCallsVisible,
      t
    ),
  });
  const agentHourlyQuery = useQuery({
    queryKey: [
      'reports',
      'advanced',
      'agent-hourly',
      agentHourlyRequestFilters,
    ],
    queryFn: async () =>
      ReportsService.queryAgentHourlyConnectedCalls(agentHourlyRequestFilters!),
    enabled: isAdvancedReportQueryEnabled(
      agentHourlyRequestFilters,
      agentHourlyVisible,
      t
    ),
  });
  const outgoingVolumeQuery = useQuery({
    queryKey: [
      'reports',
      'advanced',
      'outgoing-volume',
      outgoingHeatmapRequestFilters,
    ],
    queryFn: async () =>
      ReportsService.queryOutgoingCallVolume(outgoingHeatmapRequestFilters!),
    enabled:
      isAdvancedReportQueryEnabled(
        outgoingHeatmapRequestFilters,
        outgoingHeatmapVisible,
        t
      ) && outgoingHeatmapMetric === 'volume',
  });
  const outgoingContactabilityQuery = useQuery({
    queryKey: [
      'reports',
      'advanced',
      'outgoing-contactability',
      outgoingHeatmapRequestFilters,
    ],
    queryFn: async () =>
      ReportsService.queryOutgoingCallContactability(
        outgoingHeatmapRequestFilters!
      ),
    enabled:
      isAdvancedReportQueryEnabled(
        outgoingHeatmapRequestFilters,
        outgoingHeatmapVisible,
        t
      ) &&
      Boolean(configQuery.data?.showContactability) &&
      outgoingHeatmapMetric === 'contactability',
  });
  const objectionsQuery = useQuery({
    queryKey: ['reports', 'advanced', 'objections', objectionsRequestFilters],
    queryFn: async () =>
      ReportsService.queryObjections(objectionsRequestFilters!),
    enabled: isAdvancedReportQueryEnabled(
      objectionsRequestFilters,
      objectionsVisible,
      t
    ),
  });
  const leadQualificationQuery = useQuery({
    queryKey: [
      'reports',
      'advanced',
      'lead-qualification',
      leadQualificationRequestFilters,
    ],
    queryFn: async () =>
      ReportsService.queryLeadQualification(leadQualificationRequestFilters!),
    enabled: isAdvancedReportQueryEnabled(
      leadQualificationRequestFilters,
      leadQualificationVisible,
      t
    ),
  });
  const connectedMinutesQuery = useQuery({
    queryKey: [
      'reports',
      'advanced',
      'connected-minutes',
      connectedMinutesRequestFilters,
    ],
    queryFn: async () =>
      ReportsService.queryConnectedMinutes(connectedMinutesRequestFilters!),
    enabled: isAdvancedReportQueryEnabled(
      connectedMinutesRequestFilters,
      connectedMinutesVisible,
      t
    ),
  });
  const extractionsQuery = useQuery({
    queryKey: ['reports', 'advanced', 'extractions', extractionsRequestFilters],
    queryFn: async () =>
      ReportsService.queryExtractions(extractionsRequestFilters!),
    enabled: isAdvancedReportQueryEnabled(
      extractionsRequestFilters,
      extractionsVisible,
      t
    ),
  });
  const contactabilityByPhoneQuery = useQuery({
    queryKey: [
      'reports',
      'advanced',
      'contactability-by-phone',
      contactabilityByPhoneRequestFilters,
    ],
    queryFn: async () =>
      ReportsService.queryContactabilityByPhone(
        contactabilityByPhoneRequestFilters!
      ),
    enabled:
      isAdvancedReportQueryEnabled(
        contactabilityByPhoneRequestFilters,
        contactabilityByPhoneVisible,
        t
      ) && Boolean(configQuery.data?.showContactability),
  });

  const agentCallsChartData = useMemo(() => {
    if (!agentCallsQuery.data) {
      return null;
    }

    return buildMultiSeriesLineData(agentCallsQuery.data.series);
  }, [agentCallsQuery.data]);

  const featuredCallsChartData = useMemo(() => {
    if (!featuredCallsQuery.data) {
      return null;
    }

    return buildMultiSeriesLineData(featuredCallsQuery.data.series);
  }, [featuredCallsQuery.data]);

  const leadQualificationChartData = useMemo(() => {
    if (!leadQualificationQuery.data) {
      return null;
    }

    return buildStackedBarData(leadQualificationQuery.data.series);
  }, [leadQualificationQuery.data]);

  const connectedMinutesChartData = useMemo(() => {
    if (!connectedMinutesQuery.data) {
      return null;
    }

    const useExactMinutes = configQuery.data?.showExactTotalMinutes ?? true;
    return buildSingleMetricLineData(
      connectedMinutesQuery.data.points.map((point) => point.label),
      connectedMinutesQuery.data.points.map((point) =>
        useExactMinutes ? point.exactMinutes : point.roundedMinutes
      ),
      useExactMinutes
        ? t('reports.advanced.metrics.exactMinutes')
        : t('reports.advanced.metrics.roundedMinutes')
    );
  }, [connectedMinutesQuery.data, configQuery.data?.showExactTotalMinutes, t]);

  const extractionsChartData = useMemo(() => {
    if (!extractionsQuery.data) {
      return null;
    }

    const topRows = extractionsQuery.data.rows.slice(0, 10);
    return buildSingleMetricBarData(
      topRows.map((row) => row.description),
      topRows.map((row) => row.count),
      t('reports.advanced.metrics.extractions')
    );
  }, [extractionsQuery.data, t]);

  const contactabilityByPhoneChartData = useMemo(() => {
    if (!contactabilityByPhoneQuery.data) {
      return null;
    }

    const rows = contactabilityByPhoneQuery.data.rows;
    const phoneTotals = new Map<string, number>();
    const phoneDateRatios = new Map<string, number>();
    for (const row of rows) {
      phoneTotals.set(
        row.phone,
        (phoneTotals.get(row.phone) ?? 0) + row.totalCalls
      );
      phoneDateRatios.set(`${row.phone}__${row.date}`, row.ratio);
    }

    const topPhones = [...phoneTotals.entries()]
      .sort((left, right) => right[1] - left[1])
      .slice(0, 6)
      .map(([phone]) => phone);
    const labels = [...new Set(rows.map((row) => row.date))];

    return {
      labels,
      datasets: topPhones.map((phone, index) => ({
        label: phone,
        data: labels.map(
          (label) => phoneDateRatios.get(`${phone}__${label}`) ?? 0
        ),
        borderColor: SERIES_COLORS[index % SERIES_COLORS.length],
        backgroundColor: SERIES_COLORS[index % SERIES_COLORS.length],
        tension: 0.25,
      })),
    } satisfies ChartData<'line'>;
  }, [contactabilityByPhoneQuery.data]);

  const agentPerformanceRows = useMemo(
    () => agentPerformanceQuery.data?.rows ?? [],
    [agentPerformanceQuery.data?.rows]
  );

  const sortedAgentPerformanceRows = useMemo(() => {
    const rows = [...agentPerformanceRows];

    rows.sort((left, right) => {
      let comparison = 0;

      switch (agentPerformanceSortField) {
        case 'agentName':
          comparison = compareNullableStrings(
            left.agentName,
            right.agentName,
            agentPerformanceSortDirection,
            language
          );
          break;
        case 'teamName':
          comparison = compareNullableStrings(
            left.teamName,
            right.teamName,
            agentPerformanceSortDirection,
            language
          );
          break;
        case 'connectedCalls':
          comparison = compareNullableNumbers(
            left.connectedCalls,
            right.connectedCalls,
            agentPerformanceSortDirection
          );
          break;
        case 'averageDurationSeconds':
          comparison = compareNullableNumbers(
            left.averageDurationSeconds,
            right.averageDurationSeconds,
            agentPerformanceSortDirection
          );
          break;
        case 'minutesTalked':
          comparison = compareNullableNumbers(
            left.minutesTalked,
            right.minutesTalked,
            agentPerformanceSortDirection
          );
          break;
        case 'totalCalls':
          comparison = compareNullableNumbers(
            left.totalCalls,
            right.totalCalls,
            agentPerformanceSortDirection
          );
          break;
        case 'leadCount':
          comparison = compareNullableNumbers(
            left.leadCount,
            right.leadCount,
            agentPerformanceSortDirection
          );
          break;
        case 'contactabilityPercent':
          comparison = compareNullableNumbers(
            left.contactabilityPercent,
            right.contactabilityPercent,
            agentPerformanceSortDirection
          );
          break;
        case 'callContactabilityPercent':
          comparison = compareNullableNumbers(
            left.callContactabilityPercent,
            right.callContactabilityPercent,
            agentPerformanceSortDirection
          );
          break;
        case 'callsPerLead':
          comparison = compareNullableNumbers(
            left.callsPerLead,
            right.callsPerLead,
            agentPerformanceSortDirection
          );
          break;
        case 'performance':
          comparison = compareNullableNumbers(
            left.performance,
            right.performance,
            agentPerformanceSortDirection
          );
          break;
        default:
          comparison = 0;
      }

      if (comparison !== 0) {
        return comparison;
      }

      return compareNullableStrings(
        left.agentName,
        right.agentName,
        'asc',
        language
      );
    });

    return rows;
  }, [
    agentPerformanceRows,
    agentPerformanceSortDirection,
    agentPerformanceSortField,
    language,
  ]);

  const agentPerformanceSummary = useMemo<AgentPerformanceSummaryItem[]>(() => {
    const totalCalls = agentPerformanceRows.reduce(
      (sum, row) => sum + row.totalCalls,
      0
    );
    const totalMinutes = agentPerformanceRows.reduce(
      (sum, row) => sum + (row.minutesTalked ?? 0),
      0
    );
    const totalConnectedCalls = agentPerformanceRows.reduce(
      (sum, row) => sum + row.connectedCalls,
      0
    );
    const rowCount = agentPerformanceRows.length;

    return [
      {
        label: t('reports.advanced.summary.agents'),
        value: integer(rowCount),
      },
      {
        label: t('reports.advanced.summary.totalCalls'),
        value: integer(totalCalls),
      },
      {
        label: t('reports.advanced.summary.averageCallsPerAgent'),
        value: decimal(rowCount > 0 ? totalCalls / rowCount : null, 1),
      },
      {
        label: t('reports.advanced.summary.minutesOnline'),
        value: decimal(totalMinutes, 1),
      },
      {
        label: t('reports.advanced.summary.averageConnectedPerAgent'),
        value: decimal(rowCount > 0 ? totalConnectedCalls / rowCount : null, 1),
      },
    ];
  }, [agentPerformanceRows, t]);

  const handleAgentPerformanceSort = useCallback(
    (field: AgentPerformanceSortField) => {
      setAgentPerformanceSort((currentSort) => {
        if (currentSort.field === field) {
          return {
            field,
            direction: currentSort.direction === 'asc' ? 'desc' : 'asc',
          };
        }

        return { field, direction: 'desc' };
      });
    },
    []
  );

  const renderSortablePerformanceHeader = useCallback(
    (
      label: string,
      field: AgentPerformanceSortField,
      labelClassName?: string
    ) => (
      <TableSortableHeader<AgentPerformanceSortField>
        label={label}
        field={field}
        activeField={agentPerformanceSortField}
        direction={agentPerformanceSortDirection}
        onSort={handleAgentPerformanceSort}
        variant="secondary"
        labelClassName={labelClassName}
        labels={sortableHeaderLabels}
      />
    ),
    [
      agentPerformanceSortDirection,
      agentPerformanceSortField,
      handleAgentPerformanceSort,
      sortableHeaderLabels,
    ]
  );

  const performanceColumns = useMemo(() => {
    const baseColumns = [
      {
        header: renderSortablePerformanceHeader(
          t('reports.advanced.table.headers.agent'),
          'agentName'
        ),
        accessor: 'agentName',
      },
      {
        header: renderSortablePerformanceHeader(
          t('reports.advanced.table.headers.team'),
          'teamName'
        ),
        accessor: 'teamName',
      },
      {
        header: t('reports.advanced.table.headers.profile'),
        accessor: 'profile',
      },
      {
        header: renderSortablePerformanceHeader(
          t('reports.advanced.table.headers.connected'),
          'connectedCalls'
        ),
        accessor: 'connectedCalls',
        render: (value: unknown) => integer(value as number),
      },
      {
        header: t('reports.advanced.table.headers.voicemail'),
        accessor: 'voicemailCalls',
        render: (value: unknown) => integer(value as number),
      },
      {
        header: t('reports.advanced.table.headers.noAnswer'),
        accessor: 'noAnswerCalls',
        render: (value: unknown) => integer(value as number),
      },
      {
        header: t('reports.advanced.table.headers.rejected'),
        accessor: 'rejectedCalls',
        render: (value: unknown) => integer(value as number),
      },
      {
        header: renderSortablePerformanceHeader(
          t('reports.advanced.table.headers.averageDuration'),
          'averageDurationSeconds'
        ),
        accessor: 'averageDurationSeconds',
        render: (value: unknown) => decimal(value as number | null, 1),
      },
      {
        header: renderSortablePerformanceHeader(
          t('reports.advanced.table.headers.minutesOnline'),
          'minutesTalked',
          METRIC_HEADER_LABEL_CLASS_NAME
        ),
        accessor: 'minutesTalked',
        render: (value: unknown) => (
          <span className={METRIC_CELL_VALUE_CLASS_NAME}>
            {decimal(value as number | null, 1)}
          </span>
        ),
      },
      {
        header: renderSortablePerformanceHeader(
          t('reports.advanced.table.headers.totalCalls'),
          'totalCalls',
          METRIC_HEADER_LABEL_CLASS_NAME
        ),
        accessor: 'totalCalls',
        render: (value: unknown) => (
          <span className={METRIC_CELL_VALUE_CLASS_NAME}>
            {integer(value as number)}
          </span>
        ),
      },
      {
        header: renderSortablePerformanceHeader(
          t('reports.advanced.table.headers.leads'),
          'leadCount'
        ),
        accessor: 'leadCount',
        render: (value: unknown) => integer(value as number),
      },
    ];

    const contactabilityColumns =
      configQuery.data?.showContactability === false
        ? []
        : [
            {
              header: renderSortablePerformanceHeader(
                t('reports.advanced.table.headers.leadContactability'),
                'contactabilityPercent'
              ),
              accessor: 'contactabilityPercent',
              render: (value: unknown) =>
                value === null || value === undefined
                  ? '—'
                  : `${Number(value).toFixed(2)}%`,
            },
            {
              header: renderSortablePerformanceHeader(
                t('reports.advanced.table.headers.callContactability'),
                'callContactabilityPercent'
              ),
              accessor: 'callContactabilityPercent',
              render: (value: unknown) =>
                value === null || value === undefined
                  ? '—'
                  : `${Number(value).toFixed(2)}%`,
            },
          ];

    return [
      ...baseColumns,
      ...contactabilityColumns,
      {
        header: renderSortablePerformanceHeader(
          t('reports.advanced.table.headers.callsPerLead'),
          'callsPerLead'
        ),
        accessor: 'callsPerLead',
        render: (value: unknown) => decimal(value as number | null, 2),
      },
      {
        header: t('reports.advanced.table.headers.expertise'),
        accessor: 'expertise',
        render: (value: unknown) => decimal(value as number | null, 2),
      },
      {
        header: t('reports.advanced.table.headers.confidence'),
        accessor: 'confidenceGenerated',
        render: (value: unknown) => decimal(value as number | null, 2),
      },
      {
        header: t('reports.advanced.table.headers.objections'),
        accessor: 'objectionHandling',
        render: (value: unknown) => decimal(value as number | null, 2),
      },
      {
        header: t('reports.advanced.table.headers.closing'),
        accessor: 'callClosing',
        render: (value: unknown) => decimal(value as number | null, 2),
      },
      {
        header: renderSortablePerformanceHeader(
          t('reports.advanced.table.headers.performance'),
          'performance'
        ),
        accessor: 'performance',
        render: (value: unknown) => decimal(value as number | null, 2),
      },
    ];
  }, [
    configQuery.data?.showContactability,
    renderSortablePerformanceHeader,
    t,
  ]);

  const agentPerformanceCsvLabels = useMemo(
    () => ({
      index: t('reports.advanced.agentPerformance.export.headers.index'),
      agent: t('reports.advanced.agentPerformance.export.headers.agent'),
      connectedEffective: t(
        'reports.advanced.agentPerformance.export.headers.connectedEffective'
      ),
      connectedVoicemail: t(
        'reports.advanced.agentPerformance.export.headers.connectedVoicemail'
      ),
      noAnswer: t('reports.advanced.agentPerformance.export.headers.noAnswer'),
      rejected: t('reports.advanced.agentPerformance.export.headers.rejected'),
      total: t('reports.advanced.agentPerformance.export.headers.total'),
      averageDuration: t(
        'reports.advanced.agentPerformance.export.headers.averageDuration'
      ),
      minutesTalked: t(
        'reports.advanced.agentPerformance.export.headers.minutesTalked'
      ),
      leadCount: t(
        'reports.advanced.agentPerformance.export.headers.leadCount'
      ),
      leadContactability: t(
        'reports.advanced.agentPerformance.export.headers.leadContactability'
      ),
      callContactability: t(
        'reports.advanced.agentPerformance.export.headers.callContactability'
      ),
      callsPerLead: t(
        'reports.advanced.agentPerformance.export.headers.callsPerLead'
      ),
      expertise: t(
        'reports.advanced.agentPerformance.export.headers.expertise'
      ),
      confidence: t(
        'reports.advanced.agentPerformance.export.headers.confidence'
      ),
      objections: t(
        'reports.advanced.agentPerformance.export.headers.objections'
      ),
      closing: t('reports.advanced.agentPerformance.export.headers.closing'),
      totalPerformance: t(
        'reports.advanced.agentPerformance.export.headers.totalPerformance'
      ),
      team: t('reports.advanced.agentPerformance.export.headers.team'),
    }),
    [t]
  );

  const {
    hasRows: hasAgentPerformanceRows,
    handleDownload: handleDownloadAgentPerformanceCsv,
  } = useAgentPerformanceCsvExport({
    response: agentPerformanceQuery.data,
    rows: sortedAgentPerformanceRows,
    config: configQuery.data,
    labels: agentPerformanceCsvLabels,
  });

  if (loading) {
    return <AdvancedReportsSkeleton />;
  }

  if (error || !configQuery.data) {
    return <InfoError error={error ?? undefined} />;
  }

  return (
    <div className="space-y-6 overflow-x-hidden px-1 pb-4">
      <section className="sticky top-0 z-30 rounded-2xl border border-neutral-200 bg-white/95 px-4 py-4 shadow-sm backdrop-blur sm:px-5">
        <div className="mx-auto grid max-w-[1800px] min-w-0 grid-cols-[repeat(auto-fit,minmax(min(100%,20rem),1fr))] items-center gap-2">
          <Text as="h2" size="h6" weight="semibold">
            {t('reports.advanced.dateRange.title')}
          </Text>
          <ReportDateRangePicker
            value={reportDateRange}
            timezone={configQuery.data.timezone}
            className="w-full min-w-0 sm:max-w-[40rem] sm:justify-self-end"
            triggerClassName="w-full"
            onApply={(dateRange) => {
              setReportDateRange(dateRange);
              setObjectionsFilters((current) =>
                current ? { ...current, page: 1 } : current
              );
            }}
          />
          <Text size="small" className="col-span-full text-neutral-500">
            {t('reports.advanced.dateRange.description')}
          </Text>
        </div>
      </section>

      <SectionCard
        sectionRef={agentPerformanceRef}
        title={t('reports.advanced.sections.agentPerformance.title')}
        description={t(
          'reports.advanced.sections.agentPerformance.description'
        )}
        actions={
          <Button
            type="button"
            size="sm"
            variant="secondary"
            appearance="outline"
            onClick={handleDownloadAgentPerformanceCsv}
            disabled={!hasAgentPerformanceRows}
            aria-label={t('reports.advanced.agentPerformance.export.button')}
          >
            <PiDownloadSimple className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t('reports.advanced.agentPerformance.export.button')}
            </span>
          </Button>
        }
      >
        <div
          className={`${REPORT_FILTER_ROW_CLASS_NAME} border-b border-neutral-100 bg-neutral-50 px-4 py-4`}
        >
          <Select
            size="sm"
            containerClassName={REPORT_SELECT_MEDIUM_WIDTH_CLASS_NAME}
            value={agentPerformanceFilters!.callDirection}
            onChange={(event) =>
              setAgentPerformanceFilters((current) =>
                current
                  ? {
                      ...current,
                      callDirection: event.target
                        .value as AdvancedReportsCallDirection,
                    }
                  : current
              )
            }
            options={directionOptions.map((option) => ({
              content: option.label,
              value: option.value,
            }))}
          />
          <Select
            size="sm"
            containerClassName={REPORT_SELECT_MEDIUM_WIDTH_CLASS_NAME}
            value={agentPerformanceFilters!.profileMode}
            onChange={(event) =>
              setAgentPerformanceFilters((current) =>
                current
                  ? {
                      ...current,
                      profileMode: event.target
                        .value as AdvancedReportsAgentProfileMode,
                    }
                  : current
              )
            }
            options={profileOptions.map((option) => ({
              content: option.label,
              value: option.value,
            }))}
          />
          {configQuery.data.hasTeams ? (
            <MultiSelect
              size="sm"
              disabled={teamsUnavailable}
              placeholder={t('reports.advanced.filters.teamsPlaceholder')}
              containerClassName={REPORT_SELECT_WIDE_WIDTH_CLASS_NAME}
              options={teamOptions}
              value={(agentPerformanceFilters?.teamIds ?? []).map(String)}
              onChange={(value) =>
                setAgentPerformanceFilters((current) =>
                  current ? { ...current, teamIds: value.map(Number) } : current
                )
              }
            />
          ) : null}
        </div>
        <SectionContent
          loading={!agentPerformanceVisible || agentPerformanceQuery.isLoading}
          error={resolveQueryError(agentPerformanceQuery.error)}
          hasData={Boolean(agentPerformanceQuery.data)}
        >
          <div className={AGENT_PERFORMANCE_SUMMARY_GRID_CLASS_NAME}>
            {agentPerformanceSummary.map((item) => (
              <div
                key={item.label}
                className="rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3"
              >
                <Text size="small" className="text-neutral-500">
                  {item.label}
                </Text>
                <Text
                  as="p"
                  size="h6"
                  weight="semibold"
                  className="mt-1 text-neutral-900"
                >
                  {item.value}
                </Text>
              </div>
            ))}
          </div>
          <div className="px-4 pt-3">
            <Text size="small" className="text-neutral-500">
              {t('reports.advanced.summary.note')}
            </Text>
          </div>
          <PaginatedTable
            columns={performanceColumns}
            data={sortedAgentPerformanceRows}
            keyExtractor={(row) => String(row.userId)}
            initialPageSize={10}
            fixedFirstColumn
            tableContainerClassName="max-h-[560px]"
            tableClassName="w-full min-w-[1740px]"
          />
        </SectionContent>
      </SectionCard>

      <div className="grid gap-6 xl:grid-cols-2">
        <SectionCard
          sectionRef={agentCallsRef}
          title={t('reports.advanced.sections.agentCalls.title')}
          description={t('reports.advanced.sections.agentCalls.description')}
        >
          <div
            className={`${REPORT_FILTER_ROW_CLASS_NAME} border-b border-neutral-100 bg-neutral-50 px-4 py-4`}
          >
            <Select
              size="sm"
              containerClassName={REPORT_SELECT_NARROW_WIDTH_CLASS_NAME}
              value={agentCallsFilters!.groupBy}
              onChange={(event) =>
                setAgentCallsFilters((current) =>
                  current
                    ? {
                        ...current,
                        groupBy: event.target
                          .value as AdvancedReportsTimeGroupBy,
                      }
                    : current
                )
              }
              options={groupByOptions.map((option) => ({
                content: option.label,
                value: option.value,
              }))}
            />
          </div>
          <SectionContent
            loading={!agentCallsVisible || agentCallsQuery.isLoading}
            error={resolveQueryError(agentCallsQuery.error)}
            hasData={Boolean(agentCallsQuery.data)}
          >
            <div className="h-[320px] px-4 py-4">
              {agentCallsChartData ? (
                <Line
                  data={agentCallsChartData}
                  options={SIMPLE_LINE_OPTIONS}
                />
              ) : null}
            </div>
          </SectionContent>
        </SectionCard>

        <SectionCard
          sectionRef={featuredCallsRef}
          title={t('reports.advanced.sections.featuredCalls.title')}
          description={t('reports.advanced.sections.featuredCalls.description')}
        >
          <div
            className={`${REPORT_FILTER_ROW_CLASS_NAME} border-b border-neutral-100 bg-neutral-50 px-4 py-4`}
          >
            <Select
              size="sm"
              containerClassName={REPORT_SELECT_NARROW_WIDTH_CLASS_NAME}
              value={featuredCallsFilters!.groupBy}
              onChange={(event) =>
                setFeaturedCallsFilters((current) =>
                  current
                    ? {
                        ...current,
                        groupBy: event.target
                          .value as AdvancedReportsTimeGroupBy,
                      }
                    : current
                )
              }
              options={groupByOptions.map((option) => ({
                content: option.label,
                value: option.value,
              }))}
            />
          </div>
          <SectionContent
            loading={!featuredCallsVisible || featuredCallsQuery.isLoading}
            error={resolveQueryError(featuredCallsQuery.error)}
            hasData={Boolean(featuredCallsQuery.data)}
          >
            <div className="h-[320px] px-4 py-4">
              {featuredCallsChartData ? (
                <Line
                  data={featuredCallsChartData}
                  options={SIMPLE_LINE_OPTIONS}
                />
              ) : null}
            </div>
          </SectionContent>
        </SectionCard>
      </div>

      <SectionCard
        sectionRef={agentHourlyRef}
        title={t('reports.advanced.sections.agentHourly.title')}
        description={t('reports.advanced.sections.agentHourly.description')}
      >
        <SectionContent
          loading={!agentHourlyVisible || agentHourlyQuery.isLoading}
          error={resolveQueryError(agentHourlyQuery.error)}
          hasData={Boolean(agentHourlyQuery.data)}
        >
          <PaginatedTable
            columns={[
              {
                header: t('reports.advanced.table.headers.agent'),
                accessor: 'agentName',
              },
              ...(agentHourlyQuery.data?.hourLabels.map((label, index) => ({
                header: label,
                accessor: `hour-${index}`,
                render: (_value: unknown, row: { hourlyCounts: number[] }) =>
                  integer(row.hourlyCounts[index]),
              })) ?? []),
              {
                header: t('reports.advanced.table.headers.total'),
                accessor: 'totalCalls',
                render: (value: unknown) => integer(value as number),
              },
            ]}
            data={agentHourlyQuery.data?.rows ?? []}
            keyExtractor={(row) => String(row.agentId)}
            initialPageSize={10}
            tableClassName="w-full min-w-[1480px]"
          />
        </SectionContent>
      </SectionCard>

      <SectionCard
        sectionRef={outgoingHeatmapRef}
        title={t('reports.advanced.sections.outgoingHeatmap.title')}
        description={t('reports.advanced.sections.outgoingHeatmap.description')}
      >
        <div
          className={`${REPORT_FILTER_ROW_CLASS_NAME} border-b border-neutral-100 bg-neutral-50 px-4 py-4`}
        >
          {configQuery.data.hasTeams ? (
            <MultiSelect
              size="sm"
              disabled={teamsUnavailable}
              placeholder={t('reports.advanced.filters.teamsPlaceholder')}
              containerClassName={REPORT_SELECT_WIDE_WIDTH_CLASS_NAME}
              options={teamOptions}
              value={(outgoingHeatmapFilters?.teamIds ?? []).map(String)}
              onChange={(value) =>
                setOutgoingHeatmapFilters((current) =>
                  current ? { ...current, teamIds: value.map(Number) } : current
                )
              }
            />
          ) : null}
          <MultiSelect
            size="sm"
            disabled={usersUnavailable}
            placeholder={t('reports.advanced.filters.agentsPlaceholder')}
            containerClassName={REPORT_SELECT_WIDE_WIDTH_CLASS_NAME}
            options={userOptions}
            value={(outgoingHeatmapFilters?.userIds ?? []).map(String)}
            onChange={(value) =>
              setOutgoingHeatmapFilters((current) =>
                current ? { ...current, userIds: value.map(Number) } : current
              )
            }
          />
          {configQuery.data.showContactability ? (
            <Tabs
              value={outgoingHeatmapMetric}
              onChange={(value) =>
                setOutgoingHeatmapMetric(value as OutgoingHeatmapMetricMode)
              }
              size="sm"
              variant="secondary"
              appearance="soft"
              className="w-auto flex-none"
            >
              <Tab
                value="volume"
                label={t('reports.advanced.tabs.callVolume')}
                className="flex-none"
              />
              <Tab
                value="contactability"
                label={t('reports.advanced.tabs.contactability')}
                className="flex-none"
              />
            </Tabs>
          ) : null}
        </div>
        <SectionContent
          loading={
            !outgoingHeatmapVisible ||
            (outgoingHeatmapMetric === 'volume'
              ? outgoingVolumeQuery.isLoading
              : outgoingContactabilityQuery.isLoading)
          }
          error={resolveQueryError(
            outgoingHeatmapMetric === 'volume'
              ? outgoingVolumeQuery.error
              : outgoingContactabilityQuery.error
          )}
          hasData={Boolean(
            outgoingHeatmapMetric === 'volume'
              ? outgoingVolumeQuery.data
              : outgoingContactabilityQuery.data
          )}
        >
          <HeatmapTable
            rows={
              outgoingHeatmapMetric === 'volume'
                ? outgoingVolumeQuery.data?.rows ?? []
                : outgoingContactabilityQuery.data?.rows ?? []
            }
            totalHeader={
              outgoingHeatmapMetric === 'volume'
                ? t('reports.advanced.table.headers.total')
                : t('reports.advanced.table.headers.average')
            }
            percentMode={outgoingHeatmapMetric === 'contactability'}
          />
        </SectionContent>
      </SectionCard>

      <div className="grid gap-6 xl:grid-cols-2">
        <SectionCard
          sectionRef={objectionsRef}
          title={t('reports.advanced.sections.objections.title')}
          description={t('reports.advanced.sections.objections.description')}
        >
          <SectionContent
            loading={!objectionsVisible || objectionsQuery.isLoading}
            error={resolveQueryError(objectionsQuery.error)}
            hasData={Boolean(objectionsQuery.data)}
          >
            <ServerPaginatedTable
              columns={[
                {
                  header: t('reports.advanced.table.headers.date'),
                  accessor: 'date',
                  render: (value: unknown) => (
                    <span className="whitespace-nowrap">
                      {formatReportTimestamp(
                        typeof value === 'string' ? value : null,
                        configQuery.data.timezone
                      )}
                    </span>
                  ),
                },
                {
                  header: t('reports.advanced.table.headers.objection'),
                  accessor: 'objection',
                },
                {
                  header: t('reports.advanced.table.headers.contact'),
                  accessor: 'contactName',
                },
                {
                  header: t('reports.advanced.table.headers.phone'),
                  accessor: 'contactPhone',
                },
                {
                  header: t('reports.advanced.table.headers.agent'),
                  accessor: 'userName',
                },
                {
                  header: t('reports.advanced.table.headers.team'),
                  accessor: 'userTeam',
                },
                {
                  header: t('reports.advanced.table.headers.open'),
                  accessor: 'contactId',
                  render: (_value: unknown, row) =>
                    row.contactId ? (
                      <Button
                        type="button"
                        size="xs"
                        appearance="soft"
                        variant="secondary"
                        onClick={() =>
                          void navigate({
                            to: '/communications',
                            search: {
                              phoneVersion: 'v1',
                              contactId: row.contactId ?? undefined,
                            },
                          })
                        }
                      >
                        {t('reports.advanced.actions.viewContact')}
                      </Button>
                    ) : (
                      '—'
                    ),
                },
              ]}
              data={objectionsQuery.data?.rows ?? []}
              keyExtractor={(row) => String(row.id)}
              page={objectionsQuery.data?.page ?? objectionsFilters!.page}
              pageSize={
                objectionsQuery.data?.pageSize ?? objectionsFilters!.pageSize
              }
              totalCount={objectionsQuery.data?.totalCount ?? 0}
              onPageChange={(page) =>
                setObjectionsFilters((current) =>
                  current ? { ...current, page } : current
                )
              }
              onPageSizeChange={(pageSize) =>
                setObjectionsFilters((current) =>
                  current
                    ? {
                        ...current,
                        page: 1,
                        pageSize,
                      }
                    : current
                )
              }
              tableClassName="w-full min-w-[1120px]"
            />
          </SectionContent>
        </SectionCard>

        <SectionCard
          sectionRef={leadQualificationRef}
          title={t('reports.advanced.sections.leadQualification.title')}
          description={t(
            'reports.advanced.sections.leadQualification.description'
          )}
        >
          <div
            className={`${REPORT_FILTER_ROW_CLASS_NAME} border-b border-neutral-100 bg-neutral-50 px-4 py-4`}
          >
            <Select
              size="sm"
              containerClassName={REPORT_SELECT_NARROW_WIDTH_CLASS_NAME}
              value={leadQualificationFilters!.groupBy}
              onChange={(event) =>
                setLeadQualificationFilters((current) =>
                  current
                    ? {
                        ...current,
                        groupBy: event.target
                          .value as AdvancedReportsTimeGroupBy,
                      }
                    : current
                )
              }
              options={groupByOptions.map((option) => ({
                content: option.label,
                value: option.value,
              }))}
            />
          </div>
          <SectionContent
            loading={
              !leadQualificationVisible || leadQualificationQuery.isLoading
            }
            error={resolveQueryError(leadQualificationQuery.error)}
            hasData={Boolean(leadQualificationQuery.data)}
          >
            <div className="h-[320px] px-4 py-4">
              {leadQualificationChartData ? (
                <Bar
                  data={leadQualificationChartData}
                  options={STACKED_BAR_OPTIONS}
                />
              ) : null}
            </div>
          </SectionContent>
        </SectionCard>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <SectionCard
          sectionRef={connectedMinutesRef}
          title={t('reports.advanced.sections.connectedMinutes.title')}
          description={t(
            'reports.advanced.sections.connectedMinutes.description'
          )}
        >
          <div
            className={`${REPORT_FILTER_ROW_CLASS_NAME} border-b border-neutral-100 bg-neutral-50 px-4 py-4`}
          >
            <Select
              size="sm"
              containerClassName={REPORT_SELECT_NARROW_WIDTH_CLASS_NAME}
              value={connectedMinutesFilters!.groupBy}
              onChange={(event) =>
                setConnectedMinutesFilters((current) =>
                  current
                    ? {
                        ...current,
                        groupBy: event.target
                          .value as AdvancedReportsTimeGroupBy,
                      }
                    : current
                )
              }
              options={groupByOptions.map((option) => ({
                content: option.label,
                value: option.value,
              }))}
            />
            {configQuery.data.hasTeams ? (
              <MultiSelect
                size="sm"
                disabled={teamsUnavailable}
                placeholder={t('reports.advanced.filters.teamsPlaceholder')}
                containerClassName={REPORT_SELECT_WIDE_WIDTH_CLASS_NAME}
                options={teamOptions}
                value={(connectedMinutesFilters?.teamIds ?? []).map(String)}
                onChange={(value) =>
                  setConnectedMinutesFilters((current) =>
                    current
                      ? { ...current, teamIds: value.map(Number) }
                      : current
                  )
                }
              />
            ) : null}
          </div>
          <SectionContent
            loading={
              !connectedMinutesVisible || connectedMinutesQuery.isLoading
            }
            error={resolveQueryError(connectedMinutesQuery.error)}
            hasData={Boolean(connectedMinutesQuery.data)}
          >
            <div className="h-[320px] px-4 py-4">
              {connectedMinutesChartData ? (
                <Line
                  data={connectedMinutesChartData}
                  options={SIMPLE_LINE_OPTIONS}
                />
              ) : null}
            </div>
          </SectionContent>
        </SectionCard>

        <SectionCard
          sectionRef={extractionsRef}
          title={t('reports.advanced.sections.extractions.title')}
          description={t('reports.advanced.sections.extractions.description')}
        >
          <div
            className={`${REPORT_FILTER_ROW_CLASS_NAME} border-b border-neutral-100 bg-neutral-50 px-4 py-4`}
          >
            {configQuery.data.hasTeams ? (
              <Select
                size="sm"
                disabled={teamsUnavailable}
                containerClassName={REPORT_SELECT_MEDIUM_WIDTH_CLASS_NAME}
                value={
                  extractionsFilters!.teamFilterMode ===
                  AdvancedReportsTeamFilterMode.TEAM
                    ? `team:${extractionsFilters!.teamId ?? ''}`
                    : extractionsFilters!.teamFilterMode
                }
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === 'all') {
                    setExtractionsFilters((current) =>
                      current
                        ? {
                            ...current,
                            teamFilterMode: AdvancedReportsTeamFilterMode.ALL,
                            teamId: undefined,
                          }
                        : current
                    );
                    return;
                  }

                  if (value === 'unassigned') {
                    setExtractionsFilters((current) =>
                      current
                        ? {
                            ...current,
                            teamFilterMode:
                              AdvancedReportsTeamFilterMode.UNASSIGNED,
                            teamId: undefined,
                          }
                        : current
                    );
                    return;
                  }

                  const nextTeamId = Number(value.replace('team:', ''));
                  setExtractionsFilters((current) =>
                    current
                      ? {
                          ...current,
                          teamFilterMode: AdvancedReportsTeamFilterMode.TEAM,
                          teamId: nextTeamId,
                        }
                      : current
                  );
                }}
                options={[
                  {
                    content: t('reports.advanced.filters.allTeams'),
                    value: 'all',
                  },
                  {
                    content: t('reports.advanced.filters.unassignedTeam'),
                    value: 'unassigned',
                  },
                  ...teamOptions.map((option) => ({
                    content: option.content,
                    value: `team:${option.value}`,
                  })),
                ]}
              />
            ) : null}
          </div>
          <SectionContent
            loading={!extractionsVisible || extractionsQuery.isLoading}
            error={resolveQueryError(extractionsQuery.error)}
            hasData={Boolean(extractionsQuery.data)}
          >
            <div className="h-[320px] px-4 py-4">
              {extractionsChartData ? (
                <Bar data={extractionsChartData} options={BAR_OPTIONS} />
              ) : null}
            </div>
          </SectionContent>
        </SectionCard>
      </div>

      {configQuery.data.showContactability ? (
        <SectionCard
          sectionRef={contactabilityByPhoneRef}
          title={t('reports.advanced.sections.contactabilityByPhone.title')}
          description={t(
            'reports.advanced.sections.contactabilityByPhone.description'
          )}
        >
          <SectionContent
            loading={
              !contactabilityByPhoneVisible ||
              contactabilityByPhoneQuery.isLoading
            }
            error={resolveQueryError(contactabilityByPhoneQuery.error)}
            hasData={Boolean(contactabilityByPhoneQuery.data)}
          >
            <div className="h-[320px] px-4 py-4">
              {contactabilityByPhoneChartData ? (
                <Line
                  data={contactabilityByPhoneChartData}
                  options={SIMPLE_LINE_OPTIONS}
                />
              ) : null}
            </div>
          </SectionContent>
        </SectionCard>
      ) : null}
    </div>
  );
};

export default AdvancedReports;
